import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';

import {
  isStepReferencesSms,
  isUpdateTopicReferencesSms,
} from '@polyai/stream/store/matchers';
import {
  createTemplate,
  deleteTemplate,
  updateTemplate,
} from '@polyai/stream/store/reducers/sms/actions';
import type { SMSTemplate } from '@polyai/stream/store/reducers/sms/types';
import { createReferenceAdapter } from '@polyai/stream/store/references';
import { assignDefinedFields } from '@polyai/stream/store/utils/assign-fields';
import { assignMetadata } from '@polyai/stream/store/utils/assign-metadata';
import {
  handleCreateFlowReferences,
  handleCreateTopicReferences,
  handleDeleteFlowReferences,
  handleDeleteFlowStepReferences,
  handleDeleteTopicReferences,
  handleImportFlowReferences,
  handleStepReferenceUpdates,
  handleTopicReferenceUpdates,
} from '@polyai/stream/store/utils/reference-helpers';

import { createFlow, deleteFlow, deleteFlowStep, importFlow } from '../flows';
import { createTopic, deleteTopic } from '../knowledge-base';
import { deleteTranslation } from '../translations/actions';

const smsAdapter = createEntityAdapter<SMSTemplate>();
const smsSelectors = smsAdapter.getSelectors();

type SmsReferences = 'topics' | 'flowSteps' | 'variables' | 'translations';
const smsReferences = createReferenceAdapter([
  'topics',
  'flowSteps',
  'variables',
  'translations',
]);

const slice = createSlice({
  name: 'sms',
  initialState: {
    templates: smsAdapter.getInitialState(),
  },
  reducers: {},
  extraReducers: builder => {
    // main reducers
    builder.addCase(createTemplate, (state, action) => {
      const template: SMSTemplate = {
        ...action.payload,
        active: true,
        createdAt: '',
        createdBy: '',
        updatedAt: '',
        updatedBy: '',
      };
      assignMetadata(action, template);
      smsAdapter.addOne(state.templates, template);
    });
    builder.addCase(updateTemplate, (state, action) => {
      const template = state.templates.entities[action.payload.id];
      if (template) {
        // TODO: assign defined fields is not recursive and does not do a deep null assertion, so remains like this for now
        if (action.payload.envPhoneNumbers) {
          if (action.payload.envPhoneNumbers.sandbox) {
            template.envPhoneNumbers.sandbox =
              action.payload.envPhoneNumbers.sandbox;
          }
          if (action.payload.envPhoneNumbers.preRelease) {
            template.envPhoneNumbers.preRelease =
              action.payload.envPhoneNumbers.preRelease;
          }
          if (action.payload.envPhoneNumbers.live) {
            template.envPhoneNumbers.live = action.payload.envPhoneNumbers.live;
          }
        }

        const { envPhoneNumbers: _, ...rest } = action.payload;

        const updatedTemplate = assignDefinedFields(
          { ...template },
          {
            ...rest,
          },
        );

        smsAdapter.upsertOne(
          state.templates,
          assignMetadata(action, {
            ...updatedTemplate,
            updatedAt: '',
            updatedBy: '',
          }),
        );
      }
    });
    builder.addCase(deleteTemplate, (state, action) => {
      const template = smsSelectors.selectById(
        state.templates,
        action.payload.id,
      );
      if (template) {
        const newTemplate = {
          ...template,
          active: false,
        };
        assignMetadata(action, newTemplate);
        smsAdapter.upsertOne(state.templates, newTemplate);
      }
    });

    // side-effect: FLOW
    builder.addCase(createFlow, (state, action) => {
      handleCreateFlowReferences<SmsReferences>(
        action.payload.steps,
        'sms',
        action.payload.id,
        smsReferences.addReference,
        state.templates,
      );
    });

    builder.addCase(importFlow, (state, action) => {
      if (action.payload.sms.length) {
        smsAdapter.addMany(
          state.templates,
          action.payload.sms.map(s => {
            const template = {
              ...s,
              active: true,
              createdAt: '',
              createdBy: '',
              updatedAt: '',
              updatedBy: '',
            };
            return assignMetadata(action, template);
          }),
        );
      }
      handleImportFlowReferences<SmsReferences>(
        action.payload.steps,
        'sms',
        action.payload.flowId,
        smsReferences.addReference,
        state.templates,
      );
    });

    builder.addCase(deleteFlow, (state, action) => {
      handleDeleteFlowReferences<SMSTemplate, SmsReferences>(
        smsSelectors.selectAll(state.templates),
        action.payload.flowId,
        smsReferences.removeReferencePrefix,
        state.templates,
      );
    });

    builder.addCase(deleteFlowStep, (state, action) => {
      handleDeleteFlowStepReferences<SMSTemplate, SmsReferences>(
        smsSelectors.selectAll(state.templates),
        action.payload.flowId,
        action.payload.stepId,
        smsReferences.removeReference,
        state.templates,
      );
    });

    builder.addCase(deleteTranslation, (state, action) => {
      const { id: translationId } = action.payload;
      for (const template of Object.values(state.templates.entities)) {
        if (template?.references?.translations?.[translationId]) {
          delete template.references.translations[translationId];
        }
      }
    });

    // side-effect: TOPIC
    builder.addCase(createTopic, (state, action) => {
      handleCreateTopicReferences<SMSTemplate, SmsReferences>(
        state.templates.entities,
        action.payload.references?.sms,
        action.payload.id,
        smsReferences.addReference,
        state.templates,
      );
    });

    builder.addCase(deleteTopic, (state, action) => {
      handleDeleteTopicReferences<SMSTemplate, SmsReferences>(
        state.templates.entities,
        action.payload.id,
        smsReferences.removeReference,
        state.templates,
      );
    });

    // matchers must be after the case reducers
    builder.addMatcher(isUpdateTopicReferencesSms, (state, action) => {
      handleTopicReferenceUpdates<SMSTemplate, SmsReferences>(
        state.templates.entities,
        action.payload.references?.sms ?? {},
        action.payload.id,
        smsReferences.addReference,
        smsReferences.removeReference,
        state.templates,
      );
    });

    builder.addMatcher(isStepReferencesSms, (state, action) => {
      handleStepReferenceUpdates<SMSTemplate, SmsReferences>(
        state.templates.entities,
        action.payload.step?.references?.sms ?? {},
        action.payload.flowId,
        action.payload.step.id,
        smsReferences.addReference,
        smsReferences.removeReference,
        state.templates,
      );
    });
  },
});

export { smsAdapter };

export type { SMSTemplate };

export { createTemplate, deleteTemplate, smsReferences, updateTemplate };

export { smsSelectors };

export default slice.reducer;
